import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { getValidSitecoreLink } from 'components/SitecoreLink';
import { useIsEditing } from 'hooks';
import type { DigizuiteAsset } from 'models/asset';
import type { ProductCard } from 'models/productCard';
import type { Field, SitecoreBlockProps, SitecoreLink } from 'models/sitecore';
import type { HTMLString, JSONObject } from 'types';
import { getEditorMargin } from 'utils/business-logic';
import { getAsset, is } from 'utils/helpers';

import Inspiration from './Inspiration';

interface Props extends SitecoreBlockProps {
	fields: {
		body?: JSONObject;
		campaignBubbleLabel?: Field<string>;
		campaignBubbleSale?: Field<boolean>;
		campaignBubbleText?: Field<string>;
		content?: Field<HTMLString>;
		datasourceName?: string;
		/** @deprecated Replaced by `content` */
		description?: Field<string>;
		heading?: Field<string>;
		isCampaignSpinner?: Field<boolean>;
		isWide?: Field<boolean>;
		mirror?: Field<boolean>;
		preventAutoplay?: Field<boolean>;
		primaryImage?: Field<DigizuiteAsset[]>;
		primaryImageSmall?: Field<DigizuiteAsset[]>;
		primaryLink?: Field<SitecoreLink>;
		products?: ProductCard[];
		quaternaryLink?: Field<SitecoreLink>;
		secondaryLink?: Field<SitecoreLink>;
		tertiaryLink?: Field<SitecoreLink>;
		video?: Field<DigizuiteAsset[]>;
	};
}

export default function InspirationBlock({ fields, params }: Props) {
	const {
		content,
		datasourceName,
		description,
		heading,
		primaryLink,
		quaternaryLink,
		secondaryLink,
		tertiaryLink,
	} = fields;
	const primaryImg = getAsset('Templated 2:1', fields.primaryImage);
	const primaryImgSmall = getAsset('Templated 1:1', fields.primaryImageSmall);

	const isEditing = useIsEditing();
	if (isEditing && !content?.value && description?.value) {
		return (
			<ComponentPlaceholder
				componentName="Inspiration"
				description="The content field should be used instead of the description field"
				className={getEditorMargin(params)}
			/>
		);
	}

	// This should require a content field as well, but since we want to use
	// description as a fallback, we can't enforce it for now
	if (!primaryImg || !heading?.value) {
		return (
			<ComponentPlaceholder
				componentName="Inspiration"
				description="Requires a primary image and a heading"
				className={getEditorMargin(params)}
			/>
		);
	}

	const video = getAsset('Source Copy', fields.video);
	const mainLinks = [primaryLink, secondaryLink, tertiaryLink, quaternaryLink]
		.map((link) => getValidSitecoreLink(link?.value))
		.filter(is.truthy);

	return (
		<Inspiration
			campaignBubbleLabel={fields.campaignBubbleLabel?.value}
			campaignBubbleSale={fields.campaignBubbleSale?.value}
			campaignBubbleText={fields.campaignBubbleText?.value}
			className={getEditorMargin(params)}
			gtmItemListId="block"
			gtmItemListName={`Block: ${datasourceName ?? 'InspirationUnknown'}`}
			heading={heading}
			headingLevel={params?.heading ?? 'h2'}
			id={params?.anchor}
			isMirrored={fields.mirror?.value}
			imageAlt={primaryImg.alt}
			imageSrc={primaryImg.src}
			links={mainLinks}
			preventAutoplay={fields.preventAutoplay?.value}
			productsRequestBody={fields.body}
			smallImageAlt={primaryImgSmall?.alt}
			smallImageSrc={primaryImgSmall?.src}
			text={content?.value ? content : description}
			video={video}
		/>
	);
}
InspirationBlock.displayName = 'InspirationBlock';
